/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Box, Button, CircularProgress, DialogActions, DialogContent, IconButton } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import { Formik } from 'formik';
import { useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';
import { selectIsRequestingAvailableServiceTiers, selectRequestingAvailableServiceTiersError } from '../../../store';
import { getAvailableServiceTiers, postServiceTierRequest } from '../../../store/serviceTierRequests/actions';
import appTheme from '../../common/theme/appTheme.json';
import ServiceTierChangeRequestDialogForm from './ServiceTierChangeRequestDialogForm';
import { isNil } from 'lodash';
import { Warning } from '@mui/icons-material';

export interface ServiceTierChangeRequestDialogProps {
  open: boolean;
  onDialogCancel: () => void;
  onDialogYes: () => void;
  terminalDisplayName: string;
  currentClinAndServiceTier: string | null | undefined;
  terminalId: string;
  formRef: any;
}

export interface ServiceTierChangeRequestFormValues {
  requesterFullName: string;
  requesterEmail: string;
  requestedServiceTier: string;
}

export interface ServiceTier {
  clin: string;
  description: string;
}

export const emailValidationRegex =
  /^(?=.{1,64}@)[A-Za-z0-9_-]+(\.[A-Za-z0-9_-]+){0,64}(\+[A-Za-z0-9_-]{1,64})?@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+){0,64}(\.[A-Za-z]{2,})$/;

const validationSchema = yup.object({
  requesterFullName: yup.string().required('Requester name is required').max(79, "Requester name is too long"),
  requesterEmail: yup.string().required('Requester email is required').matches(emailValidationRegex, 'Invalid email'),
  requestedServiceTier: yup.string().required('Requested service tier is required'),
});

const ServiceTierChangeRequestDialog = (props: ServiceTierChangeRequestDialogProps) => {
  const { open, onDialogCancel, onDialogYes, terminalDisplayName, currentClinAndServiceTier, terminalId, formRef } =
    props;
  const dispatch = useDispatch();
  const loadingAvailableServiceTiers = useSelector(selectIsRequestingAvailableServiceTiers, shallowEqual);
  const requestingAvailableServiceTiersError = useSelector(selectRequestingAvailableServiceTiersError, shallowEqual);

  // GET available service tiers on mount (if dialog is open)
  useEffect(() => {
    if (open) {
      dispatch(getAvailableServiceTiers());
    }
  }, [dispatch, open]);

  return (
    <>
      <Formik
        initialValues={{
          requesterFullName: '',
          requesterEmail: '',
          requestedServiceTier: '',
        }}
        onSubmit={(values: ServiceTierChangeRequestFormValues) => {
          dispatch(
            postServiceTierRequest(
              terminalDisplayName,
              terminalId,
              values.requestedServiceTier,
              values.requesterFullName,
              values.requesterEmail,
            ),
          );
          onDialogYes();
        }}
        validationSchema={validationSchema}
        innerRef={formRef}
      >
        {formikProps => (
          <Dialog
            open={open}
            onClose={() => {
              onDialogCancel();
            }}
            sx={{
              '.MuiDialog-paper': { width: '648px', minHeight: '656px' },
            }}
          >
            {loadingAvailableServiceTiers ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '656px',
                  width: '600px',
                  color: appTheme.buttonSelectedColor,
                }}
              >
                <CircularProgress color="inherit"></CircularProgress>
              </Box>
            ) : !isNil(requestingAvailableServiceTiersError) ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '656px',
                  width: '600px',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                  <Warning />
                  <div>Error Retrieving Available Service Tiers</div>
                  <Button
                    variant="text"
                    color="primary"
                    sx={{ zIndex: 5 }}
                    onClick={() => {
                      dispatch(getAvailableServiceTiers());
                    }}
                  >
                    RETRY
                  </Button>
                </Box>
              </Box>
            ) : (
              <>
                <DialogTitle
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontFamily: appTheme.viasatFontFamilyRegular,
                    fontStyle: 'normal',
                    fontWeight: 700,
                    fontSize: '20px',
                    lineHeight: '28px',
                    letterSpacing: '0.2px',
                    padding: '24px',
                  }}
                >
                  Service tier change request
                  <IconButton onClick={onDialogCancel} sx={{ padding: '0px' }}>
                    <CloseRoundedIcon sx={{ color: appTheme.grayTextColor }} />
                  </IconButton>
                </DialogTitle>
                <DialogContent>
                  <ServiceTierChangeRequestDialogForm
                    terminalDisplayName={terminalDisplayName}
                    currentClinAndServiceTier={currentClinAndServiceTier}
                  />
                </DialogContent>
                <DialogActions>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                      onDialogCancel();
                    }}
                    sx={{
                      height: '40px',
                      width: '98px',
                      borderRadius: '1000px',
                      backgroundColor: '#202E39',
                      color: '#ffffff',
                      fontFamily: 'Source Sans Pro',
                      fontSize: '16px',
                      fontWeight: 600,
                      lineHeight: '24px',
                      letterSpacing: 0,
                      textAlign: 'center',
                      '&:hover': {
                        backgroundColor: '#202E39',
                      },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      formikProps.submitForm();
                    }}
                    sx={{
                      height: '40px',
                      width: '98px',
                      borderRadius: '1000px',
                      backgroundColor: '#18ECFC',
                      border: 'none',
                      color: '#202E39',
                      fontFamily: 'Source Sans Pro',
                      fontSize: '16px',
                      fontWeight: 600,
                      lineHeight: '24px',
                      letterSpacing: 0,
                      textAlign: 'center',
                      '&:hover': {
                        backgroundColor: '#18ECFC',
                      },
                    }}
                  >
                    Submit
                  </Button>
                </DialogActions>
              </>
            )}
          </Dialog>
        )}
      </Formik>
    </>
  );
};
export default ServiceTierChangeRequestDialog;
